import { Document, Page, View, Text } from '@react-pdf/renderer';
import { styles } from './PDFStyles';
import { PDFHeader } from './PDFHeader';
import { PDFCategoryTable } from './PDFCategoryTable';
import { PDFFooter } from './PDFFooter';
import { PDFPlanLegend } from './PDFPlanLegend';
import { PDFPackageTable } from './PDFPackageTable';
import { PDFTagsSection } from './PDFTagsSection';
import type { SimplifiedPlan, Channel, Category } from "@/utils/previewFormatters";
import type { CurrencyCode } from "@/utils/currencyFormatter";
import { formatCurrency } from '@/utils/currencyFormatter';

interface PDFDocumentProps {
  provider: {
    id: number;
    name: string;
    has_sequence_numbers: boolean;
  };
  version: {
    id: number;
    provider_id: number;
    version_number: string;
    status: "draft" | "published";
    description: string | null;
    effective_date: string | null;
    published_at: string | null;
  };
  region?: {
    id: number;
    name: string;
    zip_code?: string;
  } | null;
  plans: SimplifiedPlan[];
  categories: Category[];
  channels: Channel[];
  packages?: Array<{
    id: number;
    name: string;
    description?: string | null;
    monthly_price?: number | null;
    yearly_price?: number | null;
    package_type?: string | null;
    is_addon?: boolean;
    is_featured?: boolean;
  }>;
  tags?: Array<{
    id: number;
    name: string;
    description?: string | null;
  }>;
  types: Array<{ id: number; name: string }>;
  providerCurrency: CurrencyCode;
  filterOptions: {
    // Channel filters
    showDescription: boolean;
    showPrice: boolean;
    showPlans: boolean;
    showInAllCategories: boolean;
    showLocalChannelsCategory: boolean;
    hideSerialNumbers: boolean;
    
    // Plan filters
    showPlanDescription: boolean;
    showPlanMonthlyPrice: boolean;
    showPlanYearlyPrice: boolean;
    
    // Category filters
    showCategoryDescription: boolean;
    showCategoryPrice: boolean;

    // Package filters
    showPackages?: boolean;
    showPackageDescription?: boolean;
    showPackageMonthlyPrice?: boolean;
    showPackageYearlyPrice?: boolean;
    showPackageType?: boolean;

    // Tag filters
    showTags?: boolean;
    showTagDescription?: boolean;
    showTagChannelCount?: boolean;
  };
}

interface SubsequentPageHeaderProps {
  provider: {
    name: string;
  };
  version: {
    version_number: string;
  };
  region?: {
    name: string;
    zip_code?: string;
  } | null;
  fixed?: boolean;
}

function SubsequentPageHeader({ provider, version, region, fixed }: SubsequentPageHeaderProps) {
  // Safely format the header text to avoid empty strings
  const providerName = provider.name || 'Unknown Provider';
  const versionNumber = version.version_number || 'Unknown';
  const regionName = region?.name ? ` - ${region.name}` : '';
  
  const headerText = `${providerName} - Version ${versionNumber}${regionName}`;

  return (
    <View style={styles.subsequentPageHeader} fixed={fixed}>
      <Text style={styles.subsequentPageTitle}>
        {headerText}
      </Text>
    </View>
  );
}

export function PDFChannelDocument({
  provider,
  version,
  region,
  plans,
  categories,
  channels,
  packages = [],
  tags = [],
  types,
  providerCurrency,
  filterOptions
}: PDFDocumentProps) {
  console.log('📄 PDFChannelDocument: Starting render...');
  console.time('PDFDocument: Data Validation');

  // Validate and sanitize input data to prevent array errors
  const safePlans = Array.isArray(plans) ? plans.filter(plan => plan && plan.id) : [];
  const safeCategories = Array.isArray(categories) ? categories.filter(cat => cat && cat.id) : [];
  const safeChannels = Array.isArray(channels) ? channels.filter(ch => ch && ch.id) : [];
  const safePackages = Array.isArray(packages) ? packages.filter(pkg => pkg && pkg.id) : [];
  const safeTags = Array.isArray(tags) ? tags.filter(tag => tag && tag.id) : [];
  const safeTypes = Array.isArray(types) ? types.filter(type => type && type.id) : [];

  console.timeEnd('PDFDocument: Data Validation');
  console.log('📄 Validated data:', {
    plans: safePlans.length,
    categories: safeCategories.length,
    channels: safeChannels.length,
    packages: safePackages.length,
    tags: safeTags.length,
    types: safeTypes.length
  });

  // Collection for local channels if the option is enabled
  const localChannels: Channel[] = [];

  // Organize channels by categories, respecting the filterOptions.showInAllCategories flag
  const channelsByCategory: Record<string | number, Channel[]> = {};

  console.time('PDFDocument: Processing Channels by Category');
  // Process channels safely
  safeChannels.forEach((channel, index) => {
    if (index % 100 === 0 && index > 0) {
      console.log(`  ↳ Processed ${index}/${safeChannels.length} channels...`);
    }
    try {
      // Check if this is a local channel and filter option is enabled
      if (filterOptions.showLocalChannelsCategory && channel.is_local) {
        localChannels.push(channel);
        
        // If we don't want the channel to appear in all categories,
        // we can return here to skip adding it to regular categories
        if (!filterOptions.showInAllCategories) {
          return;
        }
      }
      
      // Handle multiple categories (new approach)
      if (channel.categories && Array.isArray(channel.categories) && channel.categories.length > 0) {
        // Decide which categories to use based on the showInAllCategories flag
        const categoriesToUse = filterOptions.showInAllCategories 
          ? channel.categories 
          : channel.categories.filter((cc: {is_default: boolean}) => cc.is_default);
        
        categoriesToUse.forEach((channelCategory: {category: Category; category_id: number}) => {
          if (channelCategory.category && channelCategory.category.id) {
            const categoryId = channelCategory.category.id;
            if (!channelsByCategory[categoryId]) {
              channelsByCategory[categoryId] = [];
            }
            channelsByCategory[categoryId].push(channel);
          }
        });
      } 
      // Legacy fallback for single category_id field
      else if (channel.category_id) {
        const categoryId = channel.category_id;
        if (!channelsByCategory[categoryId]) {
          channelsByCategory[categoryId] = [];
        }
        channelsByCategory[categoryId].push(channel);
      } 
      // Uncategorized channels
      else {
        if (!channelsByCategory['uncategorized']) {
          channelsByCategory['uncategorized'] = [];
        }
        channelsByCategory['uncategorized'].push(channel);
      }
    } catch (error) {
      console.warn('Error processing channel:', channel.id, error);
      // Skip this channel if there's an error
    }
  });
  console.timeEnd('PDFDocument: Processing Channels by Category');
  console.log(`✅ Organized channels into ${Object.keys(channelsByCategory).length} category groups`);

  // Create content sections safely
  console.time('PDFDocument: Creating Content Sections');
  const contentSections: Array<{
    title: string;
    channels: Channel[];
    category?: Category;
  }> = [];

  // Add category sections in hierarchical order
  safeCategories.forEach((category, index) => {
    if (index % 10 === 0 && index > 0) {
      console.log(`  ↳ Creating section ${index}/${safeCategories.length}...`);
    }
    const categoryChannels = channelsByCategory[category.id] || [];
    if (categoryChannels.length > 0) {
      const isSubcategory = category.parent_id !== null;
      contentSections.push({
        title: isSubcategory ? `    ${category.name}` : category.name || 'Unnamed Category',
        channels: categoryChannels,
        category
      });
    }
  });

  // Add local channels section if option is enabled and local channels exist
  if (filterOptions.showLocalChannelsCategory && localChannels.length > 0) {
    contentSections.push({
      title: "Local Channels",
      channels: localChannels
    });
  }

  // Add uncategorized channels
  const uncategorizedChannels = channelsByCategory['uncategorized'] || [];
  if (uncategorizedChannels.length > 0) {
    contentSections.push({
      title: "Uncategorized Channels",
      channels: uncategorizedChannels
    });
  }

  console.timeEnd('PDFDocument: Creating Content Sections');
  console.log(`✅ Created ${contentSections.length} content sections`);

  // Split content sections into chunks to avoid yoga layout overflow
  // Each chunk will be on separate pages to reduce memory pressure
  const SECTIONS_PER_PAGE = 5; // Process 5 categories per page max
  const sectionChunks: typeof contentSections[] = [];

  for (let i = 0; i < contentSections.length; i += SECTIONS_PER_PAGE) {
    sectionChunks.push(contentSections.slice(i, i + SECTIONS_PER_PAGE));
  }

  console.log(`📄 Split into ${sectionChunks.length} page chunks to prevent memory overflow`);
  console.log('📄 PDFChannelDocument: Starting JSX render...');

  return (
    <Document>
      {/* First page dedicated to information and legend */}
      <Page size="A4" style={styles.page}>
        <PDFHeader
          provider={provider}
          version={version}
          region={region}
        />

        {/* Show plan legend when plans are enabled */}
        {filterOptions.showPlans && safePlans.length > 0 && (
          <PDFPlanLegend
            plans={safePlans}
            filterOptions={{
              showPlanDescription: filterOptions.showPlanDescription,
              showPlanMonthlyPrice: filterOptions.showPlanMonthlyPrice,
              showPlanYearlyPrice: filterOptions.showPlanYearlyPrice
            }}
            currency={providerCurrency}
          />
        )}

        {/* Show packages when enabled */}
        {filterOptions.showPackages && safePackages.length > 0 && (
          <PDFPackageTable
            packages={safePackages}
            providerCurrency={providerCurrency}
            filterOptions={{
              showPackageDescription: filterOptions.showPackageDescription,
              showPackageMonthlyPrice: filterOptions.showPackageMonthlyPrice,
              showPackageYearlyPrice: filterOptions.showPackageYearlyPrice,
              showPackageType: filterOptions.showPackageType
            }}
          />
        )}

        {/* Show tags when enabled */}
        {filterOptions.showTags && safeTags.length > 0 && (
          <PDFTagsSection
            tags={safeTags}
            channels={safeChannels}
            filterOptions={{
              showTagDescription: filterOptions.showTagDescription,
              showTagChannelCount: filterOptions.showTagChannelCount
            }}
          />
        )}

        {/* Provider information section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Provider Information</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>Provider Name</Text>
              </View>
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{provider.name || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>Version Number</Text>
              </View>
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{version.version_number || 'N/A'}</Text>
              </View>
            </View>
            {region && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Region</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{region.name || 'N/A'}{region.zip_code ? ` (${region.zip_code})` : ''}</Text>
                </View>
              </View>
            )}
            {version.effective_date && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Effective Date</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{new Date(version.effective_date).toLocaleDateString()}</Text>
                </View>
              </View>
            )}
            {version.description && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Description</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{version.description}</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Document summary section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Document Summary</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>Total Categories</Text>
              </View>
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{safeCategories.length.toString()}</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>Total Channels</Text>
              </View>
              <View style={[styles.tableCell, { flex: 2 }]}>
                <Text>{safeChannels.length.toString()}</Text>
              </View>
            </View>
            {filterOptions.showPlans && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Total Plans</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{safePlans.length.toString()}</Text>
                </View>
              </View>
            )}
            {filterOptions.showPackages && safePackages.length > 0 && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Total Packages</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{safePackages.length.toString()}</Text>
                </View>
              </View>
            )}
            {filterOptions.showTags && safeTags.length > 0 && (
              <View style={styles.tableRow}>
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>Total Tags</Text>
                </View>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{safeTags.length.toString()}</Text>
                </View>
              </View>
            )}
          </View>
        </View>
        
        <PDFFooter />
      </Page>

      {/* Multiple Content pages - chunked to avoid memory overflow */}
      {sectionChunks.map((chunk, chunkIndex) => (
        <Page key={`page-${chunkIndex}`} size="A4" style={styles.page} wrap>
          <SubsequentPageHeader
            provider={provider}
            version={version}
            region={region}
            fixed // Keep header fixed on all flowed pages
          />

          {/* Map through sections in this chunk */}
          {chunk.map((section, sectionIndex) => (
          <View key={`section-${sectionIndex}`} style={styles.section}>
            {/* Category Header Row: Title on Left, Price on Right */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              {/* Category Title - Different styles for parent vs child */}
              {section.category?.parent_id && section.category?.parent_name ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 15, marginTop: 3, marginBottom: 8 }}>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: '#666666' }}>
                    {section.category.parent_name || 'Category'}
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: '#666666', marginHorizontal: 4, lineHeight: 1 }}>
                    &gt;
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: '#666666' }}>
                    {section.title || 'Subcategory'}
                  </Text>
                </View>
              ) : (
                <Text style={styles.parentCategoryTitle}>
                  {section.title || 'Category'}
                </Text>
              )}
              
              {/* Category Price (if available and enabled) - Aligned Right */}
              {section.category && filterOptions.showCategoryPrice && section.category.price !== null && (
                <Text style={styles.categoryPriceValue}>
                  {formatCurrency(section.category.price, providerCurrency)}
                </Text>
              )}
            </View>
            
            {/* Category Description (if available and enabled) - Below Header */}
            {section.category && filterOptions.showCategoryDescription && section.category.description && (
              <View style={styles.categoryInfo}>
                <Text style={styles.categoryInfoLabel}>
                  Description: {section.category.description}
                </Text>
              </View>
            )}
            
            {/* Category Table */}
            <PDFCategoryTable
              channels={section.channels}
              types={safeTypes}
              hasSequenceNumbers={provider.has_sequence_numbers}
              categoryName={section.title}
              providerId={provider.id}
              providerCurrency={providerCurrency}
              filterOptions={filterOptions}
            />
          </View>
          ))}

          <PDFFooter />
        </Page>
      ))}
    </Document>
  );
}
